import { useEffect, useState } from "react";
import { Accessibility as A11yIcon, Contrast, Minus, Plus, X, RotateCcw } from "lucide-react";

const STEPS = [100, 112, 125, 140];

export const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [scaleIdx, setScaleIdx] = useState(() => {
    const s = parseInt(localStorage.getItem("hi-scale") || "100", 10);
    const i = STEPS.indexOf(s);
    return i >= 0 ? i : 0;
  });
  const [contrast, setContrast] = useState(() => localStorage.getItem("hi-contrast") === "1");

  useEffect(() => {
    const pct = STEPS[scaleIdx];
    document.documentElement.style.fontSize = `${(16 * pct) / 100}px`;
    localStorage.setItem("hi-scale", String(pct));
  }, [scaleIdx]);

  useEffect(() => {
    document.body.classList.toggle("hi-contrast", contrast);
    localStorage.setItem("hi-contrast", contrast ? "1" : "0");
  }, [contrast]);

  return (
    <div className="fixed bottom-5 right-5 z-[60] print:hidden">
      {open && (
        <div
          data-testid="a11y-panel"
          className="mb-3 w-64 rounded-2xl bg-[#0b1424] border border-white/15 shadow-2xl p-4 hi-body"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-[#f3ead4]">Accessibility</span>
            <button onClick={() => setOpen(false)} data-testid="a11y-close" className="text-white/60 hover:text-white">
              <X size={16} />
            </button>
          </div>

          <div className="mb-4">
            <div className="text-[11px] uppercase tracking-wider text-white/50 mb-2">Text size</div>
            <div className="flex items-center gap-2">
              <button
                data-testid="a11y-text-smaller"
                onClick={() => setScaleIdx((i) => Math.max(0, i - 1))}
                className="hi-cta grid place-items-center w-9 h-9 rounded-lg bg-white/10 text-white hover:bg-white/20"
                aria-label="Decrease text size"
              >
                <Minus size={16} />
              </button>
              <div className="flex-1 text-center text-sm text-white/80">{STEPS[scaleIdx]}%</div>
              <button
                data-testid="a11y-text-larger"
                onClick={() => setScaleIdx((i) => Math.min(STEPS.length - 1, i + 1))}
                className="hi-cta grid place-items-center w-9 h-9 rounded-lg bg-white/10 text-white hover:bg-white/20"
                aria-label="Increase text size"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <button
            data-testid="a11y-contrast-toggle"
            onClick={() => setContrast((c) => !c)}
            className={`hi-cta w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold ${
              contrast ? "bg-[#3b9bff] text-white" : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            <Contrast size={16} /> High contrast: {contrast ? "On" : "Off"}
          </button>

          <button
            data-testid="a11y-reset"
            onClick={() => { setScaleIdx(0); setContrast(false); }}
            className="hi-cta w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs text-white/60 hover:text-white mt-2"
          >
            <RotateCcw size={13} /> Reset
          </button>
        </div>
      )}

      <button
        data-testid="a11y-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Accessibility options"
        className="hi-cta grid place-items-center w-12 h-12 rounded-full bg-gradient-to-br from-[#1f5fbf] to-[#3b9bff] text-white shadow-[0_10px_30px_-8px_rgba(59,155,255,0.7)] hover:brightness-110"
      >
        <A11yIcon size={22} />
      </button>
    </div>
  );
};

export default AccessibilityWidget;
